import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Import TinyMCE locally to avoid cloud/API key requirement
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

@Component({
    selector: 'app-rich-editor',
    templateUrl: './rich-editor.component.html',
    styleUrls: ['./rich-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RichEditorComponent),
            multi: true
        }
    ]
})
export class RichEditorComponent implements ControlValueAccessor, OnInit {
    @Input() placeholder: string = 'Introduceți conținutul...';
    @Input() minHeight: string = '400px';
    @Input() disabled: boolean = false;

    @Output() editorReady = new EventEmitter<any>();
    @Output() contentChanged = new EventEmitter<string>();

    public editorInstance: any;

    private _value: string = '';
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    // TinyMCE 6 configuration
    public tinymceInit: any;

    constructor() {}

    ngOnInit(): void {
        this.tinymceInit = {
            base_url: '/tinymce',
            suffix: '.min',
            height: parseInt(this.minHeight) || 400,
            menubar: true,
            branding: false,
            promotion: false,
            plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount'
            ],
            toolbar: 'undo redo | styles | bold italic underline strikethrough | ' +
                'alignleft aligncenter alignright alignjustify | ' +
                'bullist numlist outdent indent | ' +
                'forecolor backcolor | ' +
                'table | link image | ' +
                'removeformat code fullscreen | help',
            toolbar_mode: 'sliding',
            content_style: `
                body {
                    font-family: 'Times New Roman', Times, serif;
                    font-size: 14px;
                    line-height: 1.6;
                    padding: 10px;
                }
                table {
                    border-collapse: collapse;
                    width: 100%;
                }
                table td, table th {
                    border: 1px solid #ccc;
                    padding: 8px;
                }
            `,
            paste_data_images: true,
            table_default_attributes: {
                border: '1'
            },
            table_default_styles: {
                'border-collapse': 'collapse',
                'width': '100%'
            },
            setup: (editor: any) => {
                this.editorInstance = editor;
                editor.on('init', () => {
                    this.editorReady.emit(editor);
                    if (this._value) {
                        editor.setContent(this._value);
                    }
                });
            }
        };
    }

    // ControlValueAccessor implementation
    writeValue(value: string): void {
        this._value = value || '';
        if (this.editorInstance) {
            this.editorInstance.setContent(this._value);
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.editorInstance) {
            this.editorInstance.mode.set(isDisabled ? 'readonly' : 'design');
        }
    }

    // Public methods
    get value(): string {
        return this._value;
    }

    set value(val: string) {
        this._value = val;
        this.onChange(val);
        this.onTouched();
    }

    onEditorChange(event: any): void {
        const content = event.editor.getContent();
        this._value = content;
        this.onChange(content);
        this.contentChanged.emit(content);
    }

    onEditorBlur(): void {
        this.onTouched();
    }

    // Utility methods for external use
    getEditorData(): string {
        return this.editorInstance ? this.editorInstance.getContent() : this._value;
    }

    setEditorData(data: string): void {
        if (this.editorInstance) {
            this.editorInstance.setContent(data);
        }
        this._value = data;
    }

    focus(): void {
        if (this.editorInstance) {
            this.editorInstance.focus();
        }
    }
}
